import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import List from "@mui/material/List";
import AddIcon from "@mui/icons-material/Add";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import WebIcon from "@mui/icons-material/Web";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import { useSelector, useDispatch } from "react-redux";
import { RootStateType, IChannel, ISubChannelSummary } from "@Interfaces";
import { ChannelActions } from "@Store";
import { ChannelItem } from "../ChannelItem";
import { ListSubheader } from "@mui/material";
import useAccountInfo from "@Hooks/useAccountInfo";
import { useTranslation } from "react-i18next";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import {
    filterActiveChannels,
    filterInactiveChannels,
    filterClosedChannels,
} from "@Utils/channelFilters";
import { hasPremiumFeatures } from "@Utils/accountEntitlements";
import { useTheme } from "@mui/material/styles";

type AdminType = { email: string; username: string; _id: string };

type ChannelContainerProps = {
    expandParentsByDefault?: boolean;
    fetchChannelsOnMount?: boolean;
    onChannelNavigate?: () => void;
    showChannelMenu?: boolean;
    showChannelAvatar?: boolean;
    showInlineSubChannelAction?: boolean;
};

export const ChannelContainer: React.FC<ChannelContainerProps> = ({
    expandParentsByDefault = false,
    fetchChannelsOnMount = true,
    onChannelNavigate,
    showChannelMenu = true,
    showChannelAvatar = true,
    showInlineSubChannelAction = false,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const { channelId } = useParams<"channelId">();
    const theme = useTheme();

    const user = useSelector((state: RootStateType) => state.auth.user);
    const channels: Array<IChannel> = useSelector(
        (state: RootStateType) => state.channels.channels
    );
    const [account] = useAccountInfo();

    useEffect(() => {
        if (fetchChannelsOnMount) {
            dispatch(ChannelActions.fetchChannels());
        }
    }, [dispatch, fetchChannelsOnMount]);

    // Safely filter channels
    const userChannels = useMemo(() => {
        if (!channels) return [];

        return (
            channels?.filter((channel) =>
                channel.admins?.some(
                    (admin: AdminType) => admin._id === user?.id
                )
            ) || []
        );
    }, [channels, user]);

    const topLevelUserChannels = useMemo(
        () => userChannels.filter((channel) => !channel.parent_channel_id),
        [userChannels]
    );

    const [expandedParents, setExpandedParents] = useState<
        Record<string, boolean>
    >({});

    // Expand/collapse the sub-channel list for a given parent channel.
    const toggleParent = (id: string) => {
        setExpandedParents((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const findChannelById = React.useCallback(
        (id?: string) => channels.find((channel) => channel._id === id),
        [channels]
    );

    const resolveParentIdentifier = (channel?: IChannel | null) => {
        if (!channel) return null;
        return (
            channel.parent_channel_id ??
            (channel.has_children ? channel._id : null)
        );
    };

    const ensureParentExpanded = React.useCallback((identifier: string) => {
        setExpandedParents((prev) =>
            prev[identifier] ? prev : { ...prev, [identifier]: true }
        );
    }, []);

    useEffect(() => {
        if (!channelId) return;

        // When selecting a sub-channel, expand its parent so the child list is visible.
        const selectedChannel = findChannelById(channelId);
        const parentIdentifier = resolveParentIdentifier(selectedChannel);

        if (!parentIdentifier) return;

        ensureParentExpanded(String(parentIdentifier));
    }, [channelId, findChannelById, ensureParentExpanded]);

    useEffect(() => {
        if (!expandParentsByDefault || topLevelUserChannels.length === 0) {
            return;
        }

        setExpandedParents((prev) => {
            const nextExpanded = { ...prev };
            let hasChanges = false;

            for (const channel of topLevelUserChannels) {
                if (!nextExpanded[channel._id]) {
                    nextExpanded[channel._id] = true;
                    hasChanges = true;
                }
            }

            return hasChanges ? nextExpanded : prev;
        });
    }, [expandParentsByDefault, topLevelUserChannels]);

    useEffect(() => {
        if (!channelId) return;

        const selectedChannel = channels.find(
            (channel) => channel._id === channelId
        );

        if (
            !selectedChannel ||
            selectedChannel.parent_channel_id ||
            !selectedChannel.has_children
        ) {
            return;
        }

        const childChannels = selectedChannel.sub_channels || [];
        const activeChildren = filterActiveChannels(childChannels);

        if (childChannels.length === 0) {
            console.error(
                `[ChannelContainer] Expected sub-channels for parent ${selectedChannel._id} but found none.`,
                { channelId: selectedChannel._id, childChannels }
            );
            return;
        }

        const firstChildId = activeChildren[0]?._id ?? childChannels[0]?._id;

        if (!firstChildId || firstChildId === channelId) {
            console.error(
                "[ChannelContainer] Unable to derive a valid first child id for auto-navigation.",
                {
                    channelId,
                    firstChildId,
                    childChannels,
                }
            );
            return;
        }

        navigate(`/channel/${firstChildId}`, { replace: true });
        dispatch(ChannelActions.ensureChannelData(firstChildId));
    }, [channelId, channels, dispatch, navigate]);

    //disabledChannelForTrial throws an error, seems to be a race condition, when account.type is undefiend
    const disabledChannelForTrial =
        hasPremiumFeatures(account?.type) &&
        account?.event_limit !== null &&
        account?.total_events_created >= account?.event_limit;

    const buildChildChannel = (
        parent: IChannel,
        summary: ISubChannelSummary
    ): IChannel => {
        const hydratedChild =
            channels.find((channel) => channel._id === summary._id) || null;

        if (hydratedChild) {
            return {
                ...hydratedChild,
                settings: parent.settings ?? hydratedChild.settings,
            };
        }

        return {
            _id: summary._id,
            title: summary.title,
            description: summary.description ?? "",
            cover_image: parent.cover_image ?? "",
            profile_image: summary.profile_image ?? parent.profile_image,
            cover: summary.cover ?? parent.cover,
            admins: parent.admins ?? [],
            passcode: "",
            polls: [],
            published_date: "",
            shared_links: [],
            ai_context: parent.ai_context,
            settings: parent.settings,
            parent_channel_id: parent._id,
            has_children: false,
            sub_channels: null,
            active: typeof summary.active === "boolean" ? summary.active : true,
            closed:
                typeof summary.closed === "boolean"
                    ? summary.closed
                    : undefined,
            isHydrated: false,
            pollsStatus: "idle",
            detailsStatus: "idle",
        };
    };

    const renderChannelNode = (channel: IChannel) => {
        const childChannels = channel.sub_channels || [];
        const activeChildren = filterActiveChannels(childChannels);
        const inactiveChildren = filterInactiveChannels(childChannels);
        const endedChildren = filterClosedChannels(childChannels);
        const hasChildren = childChannels.length > 0;
        const parentId = String(channel._id);
        const isExpanded = expandedParents[parentId] ?? false;
        const isActive =
            channelId === channel._id ||
            childChannels.some((child) => child._id === channelId);
        const showAddSubChannelRow =
            showInlineSubChannelAction && hasChildren && !channel.closed;

        return (
            <React.Fragment key={channel._id}>
                <ChannelItem
                    channel={{ ...channel }}
                    selected={isActive}
                    hasChildren={hasChildren}
                    isExpanded={isExpanded}
                    onToggleChildren={toggleParent}
                    isParentActive={isActive}
                    onChannelNavigate={onChannelNavigate}
                    showChannelMenu={showChannelMenu}
                    showChannelAvatar={showChannelAvatar}
                />
                {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {showAddSubChannelRow && (
                                <ListItem
                                    button
                                    onClick={() =>
                                        handleOpenSubChannel(channel)
                                    }
                                    sx={{
                                        pl: 2,
                                        minHeight: theme.spacing(4.5),
                                        py: 0.75,
                                        borderLeft: `3px solid ${theme.palette.grey[300]}`,
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{ minWidth: theme.spacing(4) }}
                                    >
                                        <AddIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={t(
                                            "dialogs.channel.add_sub_channel",
                                            {
                                                defaultValue: "Add Sub-channel",
                                            }
                                        )}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontSize: "0.95rem",
                                                fontWeight: 600,
                                            },
                                        }}
                                    />
                                </ListItem>
                            )}
                            {activeChildren.map((child) => (
                                <ChannelItem
                                    key={child._id}
                                    channel={buildChildChannel(channel, child)}
                                    selected={channelId === child._id}
                                    isChild
                                    onChannelNavigate={onChannelNavigate}
                                    showChannelMenu={showChannelMenu}
                                    showChannelAvatar={showChannelAvatar}
                                />
                            ))}
                            {inactiveChildren.length > 0 && (
                                <>
                                    <Divider />
                                    <ListSubheader
                                        component="div"
                                        disableSticky
                                        className="text-start"
                                        sx={{
                                            borderLeft: `3px solid ${theme.palette.grey[300]}`,
                                            pl: 2,
                                            textTransform: "uppercase",
                                            fontSize: "xx-small",
                                            lineHeight: "30px",
                                        }}
                                    >
                                        {t(
                                            "channel_container.unpublished",
                                            "Unpublished"
                                        )}
                                    </ListSubheader>
                                    {inactiveChildren.map((child) => (
                                        <ChannelItem
                                            key={child._id}
                                            channel={buildChildChannel(
                                                channel,
                                                child
                                            )}
                                            selected={channelId === child._id}
                                            isChild
                                            onChannelNavigate={
                                                onChannelNavigate
                                            }
                                            showChannelMenu={showChannelMenu}
                                            showChannelAvatar={
                                                showChannelAvatar
                                            }
                                        />
                                    ))}
                                </>
                            )}
                            {endedChildren.length > 0 && (
                                <>
                                    <Divider />
                                    <ListSubheader
                                        component="div"
                                        disableSticky
                                        className="text-start"
                                        sx={{
                                            borderLeft: `3px solid ${theme.palette.grey[400]}`,
                                            pl: 2,
                                            textTransform: "uppercase",
                                            fontSize: "xx-small",
                                            lineHeight: "30px",
                                            color: theme.palette.text.disabled,
                                        }}
                                    >
                                        {t(
                                            "channel_container.closed",
                                            "Closed"
                                        )}
                                    </ListSubheader>
                                    {endedChildren.map((child) => (
                                        <ChannelItem
                                            key={child._id}
                                            channel={buildChildChannel(
                                                channel,
                                                child
                                            )}
                                            selected={channelId === child._id}
                                            isChild
                                            onChannelNavigate={
                                                onChannelNavigate
                                            }
                                            showChannelMenu={showChannelMenu}
                                            showChannelAvatar={
                                                showChannelAvatar
                                            }
                                        />
                                    ))}
                                </>
                            )}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    const handleOpen = () => {
        onChannelNavigate?.();
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.CreateChannelDialog,
            })
        );
    };

    const handleOpenSubChannel = (parentChannel: IChannel) => {
        onChannelNavigate?.();
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.CreateChannelDialog,
                props: {
                    parentChannel,
                },
            })
        );
    };

    return (
        <>
            <List>
                <ListItem
                    button
                    onClick={handleOpen}
                    disabled={disabledChannelForTrial}
                >
                    <ListItemIcon>
                        <AddIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary={t("channel_container.create_event")}
                    />
                </ListItem>
            </List>
            <Divider />
            <ListSubheader
                disableSticky
                className="cursor-pointer"
                onClick={() => {
                    navigate("/");
                    onChannelNavigate?.();
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                    }}
                >
                    <span>{t("channel_container.overview")}</span>
                    <WebIcon />
                </div>
            </ListSubheader>
            {topLevelUserChannels.length > 0 && (
                <List
                    subheader={
                        <ListSubheader className="text-start" disableSticky>
                            {t("channel_container.my_events")}
                        </ListSubheader>
                    }
                >
                    {topLevelUserChannels.map((channel) =>
                        renderChannelNode(channel)
                    )}
                </List>
            )}
        </>
    );
};
